'use client';

// Panneau immersif des écrans d'auth (desktop) — mur de VRAIES photos souvenirs
// (jeu propre à chaque écran : sign-in / sign-up / autres = welcome) qui défile
// verticalement d'un seul bloc, en boucle sans couture.
//
// Layout ALÉATOIRE à chaque chargement (port fidèle de mobile/utils/mosaicGrid) :
// shuffle Fisher-Yates de l'ordre des photos + compositions de lignes tirées au
// hasard (25 % de lignes hautes 3-4 unités, 75 % petites 1-2), `heightUnits ≥
// largeur` (photos portrait bien cadrées). Positions, tailles et ordre DIFFÈRENT
// à chaque render. Calculé côté client (useEffect) → aucun mismatch d'hydratation.
//
// Tuiles SANS radius, jointures BLANCHES égales partout. Voile blanc en bas pour
// porter le bloc de marque centré. Décoratif (aria-hidden), respecte reduced-motion.

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useT } from '@/hooks/useT';
import { fontStyle } from '@/theme/typography';

const COLUMNS = 4;
const GUTTER = 8; // px — jointure blanche, identique partout
const TALL_RATIO = 0.25;
const MIN_UNITS = 16; // hauteur mini (unités) d'une copie → couvre le viewport pour la boucle

const SMALL_COMPS = [
  [1, 1, 1, 1],
  [2, 2],
  [2, 1, 1],
  [1, 2, 1],
  [1, 1, 2],
];
const TALL_COMPS = [[3, 1], [1, 3], [4]];

interface Tile {
  span: number;
  h: number; // heightUnits (>= span)
  src: string;
}

const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

function shuffled(n: number): number[] {
  const a = Array.from({ length: n }, (_, k) => k);
  for (let k = a.length - 1; k > 0; k -= 1) {
    const j = Math.floor(Math.random() * (k + 1));
    [a[k], a[j]] = [a[j], a[k]];
  }
  return a;
}

// Construit une mosaïque aléatoire jusqu'à avoir placé chaque photo (et couvert
// MIN_UNITS). Chaque composition somme 4 colonnes → les tuiles s'enroulent en
// lignes nettes via flex-wrap ; toutes les tuiles d'une même ligne partagent
// `h` → hauteurs alignées (aspectRatio = span/h).
function buildMosaic(srcs: string[]): { tiles: Tile[]; units: number } {
  const n = srcs.length;
  const order = shuffled(n);
  const tiles: Tile[] = [];
  let placed = 0;
  let units = 0;

  while (placed < n || units < MIN_UNITS) {
    let spans: number[];
    let h: number;
    if (Math.random() < TALL_RATIO) {
      spans = pick(TALL_COMPS);
      h = Math.max(...spans) === 4 ? 4 : pick([3, 4]);
    } else {
      spans = pick(SMALL_COMPS);
      h = Math.max(...spans) === 1 ? pick([1, 2]) : 2;
    }
    for (const span of spans) {
      tiles.push({ span, h, src: srcs[order[placed % n]] });
      placed += 1;
    }
    units += h;
  }
  return { tiles, units };
}

function MosaicCopy({ tiles }: { tiles: Tile[] }) {
  return (
    <div className="flex w-full flex-wrap">
      {tiles.map((tile, i) => (
        <div
          key={i}
          className="box-border"
          style={{ width: `${(tile.span / COLUMNS) * 100}%`, aspectRatio: `${tile.span} / ${tile.h}`, padding: GUTTER / 2 }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- photo décorative locale */}
          <img src={tile.src} alt="" aria-hidden className="block h-full w-full object-cover" loading="lazy" />
        </div>
      ))}
    </div>
  );
}

export function AuthShowcase() {
  const { t } = useT('auth');
  const pathname = usePathname();

  // Jeu de photos propre à l'écran (3 dossiers disjoints, comme le mobile).
  const set = pathname.startsWith('/sign-up')
    ? { dir: 'signup', prefix: 'u', count: 30 }
    : pathname.startsWith('/sign-in')
      ? { dir: 'signin', prefix: 's', count: 30 }
      : { dir: 'welcome', prefix: 'w', count: 80 };

  // Génération ALÉATOIRE côté client uniquement (Math.random) → nouveau patchwork
  // à chaque chargement, sans divergence SSR/hydratation.
  const [mosaic, setMosaic] = useState<{ tiles: Tile[]; units: number } | null>(null);
  useEffect(() => {
    const srcs = Array.from(
      { length: set.count },
      (_, i) => `/ambiance/${set.dir}/${set.prefix}-${String(i + 1).padStart(2, '0')}.jpg`,
    );
    // Génération client-only (Math.random) → volontairement dans un effet pour
    // éviter toute divergence SSR/hydratation ; ne boucle pas (deps stables).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMosaic(buildMosaic(srcs));
  }, [set.dir, set.prefix, set.count]);

  // Vitesse ~ constante (px/s) : plus la copie est haute, plus la boucle est longue.
  const duration = mosaic ? `${Math.round(mosaic.units * 7)}s` : '120s';

  return (
    <aside aria-hidden className="relative hidden h-full overflow-hidden bg-white lg:block">
      {mosaic ? (
        <div className="absolute inset-0 overflow-hidden">
          {/* Deux copies identiques bout à bout, translatées de -50 % → boucle sans
              couture. Padding horizontal = demi-jointure (bords = jointures internes). */}
          <div
            className="rmbr-scroll-up flex w-full flex-col will-change-transform"
            style={{ animationDuration: duration, paddingLeft: GUTTER / 2, paddingRight: GUTTER / 2 }}
          >
            <MosaicCopy tiles={mosaic.tiles} />
            <MosaicCopy tiles={mosaic.tiles} />
          </div>
        </div>
      ) : null}

      {/* Voile blanc en bas — dissout les photos vers le blanc pour porter le texte. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5"
        style={{
          background:
            'linear-gradient(to top, #FFFFFF 30%, rgba(255,255,255,0.92) 48%, rgba(255,255,255,0.55) 72%, transparent)',
        }}
      />

      {/* Marque (wordmark SVG, grand) + accroche éditoriale — CENTRÉES, ancrées en bas. */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-6 px-10 pb-16 pt-12 text-center">
        {/* eslint-disable-next-line @next/next/no-img-element -- wordmark SVG local */}
        <img
          src="/brand/wordmark_black.svg"
          alt="Remember"
          className="rmbr-reveal h-auto w-auto"
          style={{ width: 'clamp(260px, 42%, 420px)', animationDelay: '80ms' }}
        />
        <p
          className="rmbr-reveal max-w-md text-balance text-text-primary"
          style={{
            animationDelay: '200ms',
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: 'clamp(1.5rem, 2.1vw, 2.25rem)',
            lineHeight: 1.14,
            letterSpacing: '-0.015em',
          }}
        >
          {t('showcase.tagline')}
        </p>
        <span className="rmbr-reveal text-text-secondary" style={{ ...fontStyle('bodyLg'), animationDelay: '320ms' }}>
          {t('showcase.caption')}
        </span>
      </div>
    </aside>
  );
}
